import { addDays, addSeconds, differenceInSeconds, parse, parseISO, startOfDay } from 'date-fns';
import { maxBy, minBy } from 'lodash';
import moment from 'moment';
import { tz as momentTz } from 'moment-timezone';
import { tzMoment, userTimeZone } from './moment';

export const DATE_FORMAT = 'MMM DD, YYYY';
export const DATE_FORMAT_ORDINAL = 'MMM Do'; // e.g., "Dec 3rd"
export const TIME_FORMAT = 'h:mm A';
export const DATE_TIME_FORMAT = 'MMM DD, YYYY h:mm A';

/** Day is not zero padded, e.g. "Dec 3, 2026". Used by the venue portal. */
export const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

export const DATE_YYYY_MM_DD = 'YYYY-MM-DD';
export const TIME_HH_MM = 'HH:mm';

export const DATE_USED_TO_FORMAT_TIME = '2020-01-01';
const dayFormat = 'yyyy-MM-dd';
const timeFormat = `${dayFormat} HH:mm`; // zero indexed 24 hour clock

export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const API_TIME_FORMAT = 'HH:mm:ss';
export const API_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DISPLAY_DATETIME_FORMAT = `${DISPLAY_DATE_FORMAT} h:mmA`;
export const HTML_DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';
const ISO_DATETIME_FORMAT = `${API_DATE_FORMAT} ${API_TIME_FORMAT}`;

export const serializeDate = (dateStr?: string) => (dateStr ? parse(dateStr, timeFormat, new Date()) : null);

export const moveTimeToDay = (newDate: Date, dateWithWantedTime: Date) =>
    !dateWithWantedTime
        ? newDate
        : addSeconds(startOfDay(newDate), differenceInSeconds(dateWithWantedTime, startOfDay(dateWithWantedTime)));

export const moveDays = (date: Date, quantity?: number) => addDays(date, quantity || 0);

export const timeValue = (timeString: string) => timeString && Number(timeString.split(':').join(''));

export const mergeDateTime = (date: string, time: string) => `${date} ${time}`;

export const moveDayBy = (date: string, delta: number = 1) => {
    const currentDate = mergeDateTime(date, '00:00');
    const newDay = addDays(parseISO(currentDate), delta);

    return tzMoment(newDay).format('L');
};

export const adjustTime = (timeString: string) => {
    const [hours, minutes, seconds] = timeString.split(':');
    const halfHour = Number(minutes) > 0;
    const adjustedMinutes = halfHour ? '00' : '30';
    const adjustedHours = halfHour ? Number(hours) + 1 : hours;
    const resetHours = Number(adjustedHours) > 23;
    return `${resetHours ? 0 : adjustedHours}:${adjustedMinutes}:${seconds}`;
};

export const fixedTime = (baseTime: string, minTime: string) =>
    baseTime && minTime && timeValue(baseTime) <= timeValue(minTime) ? adjustTime(minTime) : baseTime;

const TIME_CHECK_FORMAT = 'H:mm:ss';
export const roundEndOfDayto12AM = (date: Date) =>
    tzMoment(date).format(TIME_CHECK_FORMAT) === '23:59:59' ? addDays(startOfDay(date), 1) : date;

export const is12AM = (date: Date) => moment(date).format(TIME_CHECK_FORMAT) === '0:00:00';

export const datetimeStrToLocalDate = (date: string, time: string) =>
    parse(`${date} ${time}`, ISO_DATETIME_FORMAT, new Date());

export const apiDateToDateObj = (date: string) => parse(date, API_DATE_FORMAT, new Date());

//ISO Dates are string sortable
export const getDateRange = (list: string[]): [Date | undefined, Date | undefined] => {
    const start = minBy(list);
    const end = maxBy(list);

    const startDate = start ? apiDateToDateObj(start) : undefined;
    const endDate = end ? apiDateToDateObj(end) : undefined;

    return [startDate, endDate];
};

/**
 * Returns the inclusive number of calendar days between start and end (e.g. Jan 1–Jan 2 = 2 days).
 * Works with both date-only (YYYY-MM-DD) and datetime strings; compares by day.
 */
export const getDateRangeDayCount = (startDate: string, endDate: string): number => {
    const start = tzMoment(startDate).startOf('day');
    const end = tzMoment(endDate).startOf('day');
    return end.diff(start, 'days') + 1;
};

export const getDateTimeStrings = (baseDate?: string | Date, zone?: string) =>
    baseDate && zone
        ? {
              date: momentTz(baseDate, zone).format(DATE_FORMAT),
              time: momentTz(baseDate, zone).format('HH:mm:ss'),
          }
        : {};

export const getShortTimeZone = (baseDate: string | Date, zone: string) => momentTz(baseDate, zone).format('z');

const timeslots = [
    '00:00:00',
    '00:30:00',
    '01:00:00',
    '01:30:00',
    '02:00:00',
    '02:30:00',
    '03:00:00',
    '03:30:00',
    '04:00:00',
    '04:30:00',
    '05:00:00',
    '05:30:00',
    '06:00:00',
    '06:30:00',
    '07:00:00',
    '07:30:00',
    '08:00:00',
    '08:30:00',
    '09:00:00',
    '09:30:00',
    '10:00:00',
    '10:30:00',
    '11:00:00',
    '11:30:00',
    '12:00:00',
    '12:30:00',
    '13:00:00',
    '13:30:00',
    '14:00:00',
    '14:30:00',
    '15:00:00',
    '15:30:00',
    '16:00:00',
    '16:30:00',
    '17:00:00',
    '17:30:00',
    '18:00:00',
    '18:30:00',
    '19:00:00',
    '19:30:00',
    '20:00:00',
    '20:30:00',
    '21:00:00',
    '21:30:00',
    '22:00:00',
    '22:30:00',
    '23:00:00',
    '23:30:00',
];
export const timeOptions = timeslots.map(slot => ({
    id: slot,
    value: slot,
    name: tzMoment(`2023-01-01T${slot}`).format('LT'),
}));

/**
 * Normalizes a time string from the API (e.g. "7:30am", "7:30 AM") to the
 * 24-hour slot format expected by `timeOptions` (e.g. "07:30:00").
 * Strings already in HH:mm:ss format are returned unchanged.
 */
export const normalizeTimeToSlot = (time: string | null | undefined): string => {
    if (!time) return '';
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
    const parsed = moment(time, ['h:mma', 'h:mm a', 'HH:mm', 'H:mm'], true);
    return parsed.isValid() ? parsed.format('HH:mm:00') : '';
};

export const isTimeStringBefore = (option: string, threshold: string | undefined) => {
    if (threshold === undefined) return false;
    return option <= threshold;
};

export const durationOptions = [
    { id: 0, name: '-' },
    { id: 5, name: '5 mins' },
    { id: 10, name: '10 mins' },
    { id: 15, name: '15 mins' },
    { id: 20, name: '20 mins' },
    { id: 25, name: '25 mins' },
    { id: 30, name: '30 mins' },
    { id: 35, name: '35 mins' },
    { id: 40, name: '40 mins' },
    { id: 45, name: '45 mins' },
    { id: 50, name: '50 mins' },
    { id: 55, name: '55 mins' },
    { id: 60, name: '1 hour' },
] as const;

export const shortTimestamp = (lastActiveAt: string) => {
    const lastSeen = tzMoment(lastActiveAt, 'UTC').tz(userTimeZone);
    const now = tzMoment('', userTimeZone);

    if (now.diff(lastSeen, 'days') < 1) {
        return lastSeen.fromNow();
    }

    if (lastSeen.month() === now.month() && lastSeen.year() === now.year()) {
        return lastSeen.format('MMM D, LT');
    }
    return lastSeen.isValid() ? lastSeen.format('ll') : '';
};

export const fullTimestamp = (isoTimestamp: string, timeZone?: string) =>
    tzMoment(isoTimestamp, timeZone).tz(userTimeZone).format('ll LT z');

export const cancelledAtTime = (meeting: BizlyAPI.Meeting) =>
    meeting.cancelledAt && `Cancelled on ${fullTimestamp(meeting.cancelledAt.date, meeting.cancelledAt.timezone)}`;

export const timeElapsed = (date?: string | Date | null | number, timeZone = 'UTC') => {
    return tzMoment(null, timeZone).isAfter(tzMoment(date, timeZone));
};

export const formatDateRange = (
    _startDate?: string | Date,
    _endDate?: string | Date,
    time = true,
    timeZone?: string
) => {
    if (!_startDate || !_endDate) return '';

    const startDate = tzMoment(_startDate, timeZone);
    const endDate = tzMoment(_endDate, timeZone);
    const tzSuffix = timeZone ? ' z' : '';

    // If the dates are the same day
    if (startDate.isSame(endDate, 'day')) {
        if (time) {
            return `${startDate.format('MMM D, YYYY h:mm A' + tzSuffix)} - ${endDate.format('h:mm A' + tzSuffix)}`;
        } else {
            return startDate.format('MMM D, YYYY');
        }
    }

    // If the dates are in the same month and year
    if (startDate.isSame(endDate, 'month') && startDate.isSame(endDate, 'year')) {
        return `${startDate.format('MMM D')}-${endDate.format('D')}, ${startDate.format('YYYY')}`;
    }

    // If the dates are in the same year but different months
    if (startDate.isSame(endDate, 'year')) {
        return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}, ${startDate.format('YYYY')}`;
    }

    // If the dates are in different years
    return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
};

export const formatTimeOnly = (time: string, timeZone = 'UTC') => {
    // An event without a time would otherwise parse "YYYY-MM-DDT" and render "Invalid date".
    if (!time) return '';
    const today = new Date().toISOString().split('T')[0];
    const dateTimeString = `${today}T${time}`; // Combine today's date with the given time
    return tzMoment(dateTimeString, timeZone).format('hh:mm A');
};

// Helper function to add 1 hour to a time string (HH:MM format)
export const addOneHour = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newHours = (hours + 1) % 24;
    return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Helper function to compare two time strings (returns true if time1 >= time2)(HH:MM format)
export const isTimeGreaterOrEqual = (startTime: string, endTime: string): boolean => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const totalStartMinutes = startHours * 60 + startMinutes;
    const totalEndMinutes = endHours * 60 + endMinutes;

    return totalStartMinutes >= totalEndMinutes;
};

/**
 * Generates an array of dates between start and end date (exclusive of end date)
 */
export const generateDateRange = (startDate: string, endDate: string): string[] => {
    const dates: string[] = [];
    const start = moment(startDate);
    const end = moment(endDate);

    // Generate dates from start to end (excluding end date for accommodations)
    const dayCount = end.diff(start, 'days');

    for (let i = 0; i < dayCount; i++) {
        const currentDate = start.clone().add(i, 'days');
        dates.push(currentDate.format('YYYY-MM-DD'));
    }

    return dates;
};

export const formatTimeHHMMSSToHHMM = (time: string) => {
    if (!time) return undefined;
    const [hours, minutes] = time.split(':');
    return `${hours}:${minutes}`;
};
